import React from "react"
import { useFormRenderer } from "./hooks"
import { DataDrivenFormSchema, HashSection, PageField } from "./schema"
import Anchor from "@/components/ui/anchor"
import { Card } from "@/components/ui/card"
import { WithErrorIndicator } from "./errors"

export function DataDrivenAnchor({
  schema,
  children,
}: {
  schema: DataDrivenFormSchema<any, PageField>
  children: React.ReactNode
}) {
  const hasAnchorSection = schema.properties.some(
    (property) => property.type === "section:hash",
  )

  if (!hasAnchorSection) return children

  const hashSections = schema.properties.filter(
    (property) => property.type === "section:hash",
  )

  return (
    <Anchor>
      <Card className="absolute h-full w-full">
        <Anchor.ScrollingVisibilityRoot className="flex h-full flex-row gap-6">
          <nav className="border-l-brand-dark-grey sticky top-0 flex w-min shrink-0 flex-col items-start overflow-y-scroll border-r-2 px-2">
            {hashSections.map((section) => (
              <DataDrivenAnchorHashLink
                key={section.sectionKey}
                section={section}
              />
            ))}
          </nav>
          <div className="grow overflow-hidden overflow-y-auto scroll-smooth">
            {children}
          </div>
        </Anchor.ScrollingVisibilityRoot>
      </Card>
    </Anchor>
  )
}

export function DataDrivenAnchorHashLink({
  section,
}: {
  section: HashSection<any, PageField>
}) {
  const { tFunc } = useFormRenderer()

  const scrollingLinks = React.useMemo(
    () =>
      section.properties
        .filter((property) => property.type === "section:scrolling")
        .map((scrollingLink) => (
          <WithErrorIndicator
            section={scrollingLink}
            key={scrollingLink.sectionKey}
          >
            <Anchor.ScrollingLink
              sectionKey={scrollingLink.sectionKey}
              title={tFunc(scrollingLink.title)}
            />
          </WithErrorIndicator>
        )),
    [section, tFunc],
  )

  return (
    <>
      <WithErrorIndicator section={section}>
        <Anchor.HashLink
          sectionKey={section.sectionKey}
          title={tFunc(section.title)}
        />
      </WithErrorIndicator>
      <Anchor.HashItemProvider sectionKey={section.sectionKey}>
        {scrollingLinks}
      </Anchor.HashItemProvider>
    </>
  )
}
